import { useEffect, useRef, useState } from 'react';
import { PRODUCT_URLS } from '@podus/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import { isPrerender } from '@/lib/isPrerender';

const CARD_TOP_OFFSET = 20; // px peek between stacked cards
const SLIDE_IN_VH = 1.5; // fraction of viewport height for each card slide-in

/**
 * Camera field of view for the stack, in degrees. Low, so the perspective reads as depth rather
 * than distortion — a buried card is genuinely further from the viewer, not merely scaled down.
 */
const STACK_FOV = 28;
/** How far back a card is pushed, in px, for each card that lands on top of it. */
const DEPTH_STEP = 30;
/**
 * How much smaller a card is as it starts its slide, as a fraction. This is object scale, never
 * depth: an arriving card has to sort in front of the stack it is covering, so it may not be pushed
 * away from the camera to look smaller.
 */
const SLIDE_SHRINK = 0.04;
/** Damping time constant, in seconds. Short enough that the stack still tracks the scroll. */
const DAMP_TAU = 0.055;

/**
 * Frosted-glass treatment for a card that is still travelling. It rides over the stack as a
 * translucent, blurred panel — you read the card beneath through it — and resolves to a solid
 * surface the moment it lands, so a settled card is never asked to composite a backdrop blur.
 */
const GLASS_BLUR = 24;
/** How far the panel's own fill is thinned at peak glass. */
const GLASS_ALPHA = 0.46;

/** Background color stops (RGB) — one per service card, subtle professional tones */
const WRAPPER_BG_COLORS: [number, number, number][] = [
    [255, 255, 255],
    [248, 249, 250], // neutral cool gray
    [255, 255, 255],
    [248, 249, 250], // neutral cool gray
    [255, 255, 255],
];

function lerpColor(a: [number, number, number], b: [number, number, number], t: number): string {
    return `rgb(${Math.round(a[0] + (b[0] - a[0]) * t)}, ${Math.round(a[1] + (b[1] - a[1]) * t)}, ${Math.round(a[2] + (b[2] - a[2]) * t)})`;
}

const services = [
    {
        brandPrefix: 'Podus AI',
        name: 'Legal Assistant',
        logo: '/logo/podus-ai.svg',
        image: '/services/legal_assistant.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Try Our Legal Assistant',
        ctaLink: PRODUCT_URLS.chatpodusai,
        bgColor: '#f5f5f5',
    },
    {
        brandPrefix: 'Podus',
        name: 'Checker',
        logo: '/logo/podus-cheaker.svg',
        image: '/services/checker.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Run a Background Check',
        ctaLink: PRODUCT_URLS.poduschecker,
        bgColor: '#f5f5f5',
    },
    {
        brandPrefix: 'Podus',
        name: 'Contracts',
        logo: '/logo/podus-contract.svg',
        image: '/services/contract.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Get Started For Free',
        ctaLink: PRODUCT_URLS.poduscontract,
        bgColor: '#f5f5f5',
    },
    {
        brandPrefix: 'Podus',
        name: 'Appointment',
        logo: '/logo/podus-appointment.svg',
        image: '/services/appointment.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Book an Appointment',
        ctaLink: PRODUCT_URLS.podusappointment,
        bgColor: '#f5f5f5',
    },
    {
        brandPrefix: 'Podus',
        name: 'Aid',
        logo: '/logo/podus-aids.svg',
        image: '/services/aids.svg',
        description:
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        cta: 'Request Aid',
        ctaLink: PRODUCT_URLS.podusaid,
        bgColor: '#f5f5f5',
    },
];

/** Find nearest scrollable ancestor */
function getScrollParent(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;
    while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === 'auto' || overflowY === 'scroll') return parent;
        parent = parent.parentElement;
    }
    return null;
}

/** Fade-up wrapper triggered by IntersectionObserver — fires once */
function AnimateOnScroll({
    children,
    className = '',
    delay = 0,
}: {
    children: React.ReactNode;
    className?: string;
    delay?: number;
}) {
    const ref = useRef<HTMLDivElement>(null);
    // Start visible during prerender so the snapshot never captures the card at opacity:0.
    const [visible, setVisible] = useState(isPrerender());

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
            }}
        >
            {children}
        </div>
    );
}

/** Mobile service card — with viewport-triggered fade-in */
function MobileServiceCard({ service, index }: { service: (typeof services)[number]; index: number }) {
    const { t } = useLanguage();
    const ref = useRef<HTMLDivElement>(null);
    // Start visible during prerender so the snapshot never captures the card at opacity:0.
    const [visible, setVisible] = useState(isPrerender());

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.3 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className="flex h-full flex-col rounded-2xl p-6"
            style={{
                backgroundColor: service.bgColor,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
                transition: 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
        >
            {/* Icon + Name */}
            <div className="mb-4 flex items-center gap-3">
                <img
                    src={service.logo}
                    alt={`${service.brandPrefix} ${service.name} icon`}
                    className="h-10 w-10 flex-shrink-0 rounded-xl object-contain"
                />
                <h3 className="text-[24px] font-bold leading-[1.1] tracking-tight sm:text-[28px]">
                    <span className="text-secondary">{service.brandPrefix} </span>
                    <span className="text-[#AEAEAE]">{service.name}</span>
                </h3>
            </div>

            {/* Description */}
            <p className="text-secondary mb-6 flex-1 text-sm font-normal leading-relaxed">
                {t(`services.${index}.description`)}
            </p>

            {/* CTA Button */}
            <a
                href={service.ctaLink}
                className="bg-secondary rounded-full px-6 py-3 text-center text-sm font-medium text-white transition-all duration-200 active:scale-[0.98]"
            >
                {t(`services.${index}.cta`)}
            </a>
        </div>
    );
}

/** Mobile horizontal snap carousel */
function MobileCarousel() {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const cards = container.querySelectorAll<HTMLElement>('[data-carousel-card]');

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        const index = Number(entry.target.getAttribute('data-index'));
                        if (!isNaN(index)) setActiveIndex(index);
                    }
                }
            },
            { root: container, threshold: 0.5 },
        );

        cards.forEach((card) => observer.observe(card));
        return () => observer.disconnect();
    }, []);

    return (
        <div>
            {/* Scrollable carousel */}
            <div
                ref={scrollRef}
                className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-6"
                style={{ scrollPaddingInline: '16px' }}
            >
                {services.map((service, i) => (
                    <div key={i} data-carousel-card data-index={i} className="w-[calc(100vw-32px)] shrink-0 snap-start">
                        <MobileServiceCard service={service} index={i} />
                    </div>
                ))}
            </div>

            {/* Dot indicators */}
            <div className="mt-2 flex items-center justify-center gap-2">
                {services.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        aria-label={`Go to service ${i + 1}`}
                        className={`rounded-full transition-all duration-300 ${
                            activeIndex === i ? 'bg-primary h-2.5 w-8' : 'bg-secondary-50 h-2.5 w-2.5'
                        }`}
                        onClick={() => {
                            const card = scrollRef.current?.children[i] as HTMLElement | undefined;
                            card?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'center',
                            });
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

export default function Services() {
    const { t } = useLanguage();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const cardsContainerRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

    const bodyRefs = useRef<(HTMLDivElement | null)[]>([]);
    const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
    const activeCardRef = useRef(-1);
    const [containerHeight, setContainerHeight] = useState(0);
    const [wrapperHeight, setWrapperHeight] = useState(`${(services.length - 1) * 70 + 100}vh`);
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
    );
    const [isTallScreen, setIsTallScreen] = useState(() => typeof window !== 'undefined' && window.innerHeight > 750);

    /* Track lg breakpoint + screen height */
    useEffect(() => {
        const mql = window.matchMedia('(min-width: 1024px)');
        const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        mql.addEventListener('change', handler);

        const onResize = () => setIsTallScreen(window.innerHeight > 750);
        window.addEventListener('resize', onResize);

        return () => {
            mql.removeEventListener('change', handler);
            window.removeEventListener('resize', onResize);
        };
    }, []);

    /* Scroll-driven stacking animation — desktop only.
     *
     * The stack is a three.js scene painted by CSS3DRenderer: the cards stay real DOM (so the copy,
     * the links and the translations are untouched), but a `CSS3DObject` per card places them in
     * world space, and the camera's perspective does the rest. A buried card is genuinely further
     * away rather than scaled down, which is what gives the depth its weight.
     *
     * Smoothness comes from the loop, not the renderer: scroll only marks the frame dirty, every
     * measurement is cached until a resize, each frame reads layout once before it writes anything,
     * and the per-card progress is damped so scroll jitter never reaches the transform.
     */
    useEffect(() => {
        /** Puts a card back on its solid fill — React's own inline values, restated. */
        const clearGlass = (index: number) => {
            const card = cardRefs.current[index];
            if (card) {
                card.style.backdropFilter = '';
                card.style.removeProperty('-webkit-backdrop-filter');
                card.style.borderRadius = '';
            }
            const body = bodyRefs.current[index];
            if (body) {
                body.style.backgroundColor = services[index].bgColor;
                body.style.boxShadow = index > 0 ? '0 -8px 30px rgba(0, 0, 0, 0.06)' : '';
            }
        };

        const restoreCards = () => {
            cardRefs.current.forEach((card, i) => {
                if (!card) return;
                card.style.transform = '';
                card.style.transformOrigin = '';
                card.style.opacity = '';
                card.style.width = '';
                card.style.height = '';
                card.style.top = `${i * CARD_TOP_OFFSET}px`;
                card.style.left = '0px';
                card.style.right = '0px';
                delete card.dataset.active;
            });
            services.forEach((_, i) => clearGlass(i));
            contentRefs.current.forEach((el) => {
                if (el) el.style.transform = '';
            });
            activeCardRef.current = -1;
        };

        if (!isDesktop) {
            setWrapperHeight('auto');
            restoreCards();
            if (wrapperRef.current) wrapperRef.current.style.backgroundColor = '';
            return;
        }

        const wrapper = wrapperRef.current;
        const container = cardsContainerRef.current;
        if (!wrapper || !container) return;

        const scrollEl = getScrollParent(wrapper);
        if (!scrollEl) return;

        let disposed = false;
        let teardown: (() => void) | null = null;

        const start = async () => {
            /** Places a card given its top edge in the container and how far back it sits. */
            type Stage = {
                place: (index: number, screenTop: number, depth: number, height: number, shrink: number) => void;
                commit: () => void;
                /** Perspective distance in px — the scale of a card at `depth` is P / (P + depth). */
                perspective: number;
                resize: (width: number, height: number) => void;
                dispose: () => void;
            };

            let stage: Stage;
            try {
                const [THREE, { CSS3DObject, CSS3DRenderer }] = await Promise.all([
                    import('three'),
                    import('three/addons/renderers/CSS3DRenderer.js'),
                ]);
                if (disposed) return;

                const scene = new THREE.Scene();
                const camera = new THREE.PerspectiveCamera(STACK_FOV, 1, 1, 20000);
                const renderer = new CSS3DRenderer();
                renderer.domElement.style.position = 'absolute';
                renderer.domElement.style.inset = '0';
                container.appendChild(renderer.domElement);

                const objects = cardRefs.current.map((card) => {
                    const object = new CSS3DObject(card!);
                    scene.add(object);
                    return object;
                });

                let perspective = 1000;
                let halfH = 0;

                stage = {
                    get perspective() {
                        return perspective;
                    },
                    resize: (width, height) => {
                        renderer.setSize(width, height);
                        camera.aspect = width / height;
                        camera.updateProjectionMatrix();
                        // CSS3DRenderer works in pixels: an object on the z = 0 plane renders at its
                        // natural size only when the camera sits exactly at the perspective distance.
                        perspective = (camera.projectionMatrix.elements[5] * height) / 2;
                        camera.position.set(0, 0, perspective);
                        halfH = height / 2;
                        cardRefs.current.forEach((card) => {
                            if (!card) return;
                            card.style.top = '0px';
                            card.style.left = '0px';
                            card.style.right = 'auto';
                            card.style.width = `${width}px`;
                        });
                    },
                    place: (index, screenTop, depth, height, shrink) => {
                        const object = objects[index];
                        if (!object) return;
                        const fromDepth = perspective / (perspective + depth);
                        const onScreen = fromDepth * shrink;
                        object.scale.setScalar(shrink);
                        // The object is centred on its position, so convert the card's top edge into
                        // a centre, then divide out the perspective scale to get back to world space.
                        object.position.set(0, (halfH - screenTop - (onScreen * height) / 2) / fromDepth, -depth);
                        object.element.style.height = `${height}px`;
                    },
                    commit: () => renderer.render(scene, camera),
                    dispose: () => {
                        objects.forEach((object) => {
                            // Hand the cards back to React's container before the stage goes away.
                            container.appendChild(object.element);
                            scene.remove(object);
                        });
                        renderer.domElement.remove();
                    },
                };
            } catch {
                // three unavailable — drive the same geometry with plain CSS transforms instead.
                if (disposed) return;
                const perspective = 1000;
                stage = {
                    perspective,
                    resize: () => {},
                    place: (index, screenTop, depth, height, shrink) => {
                        const card = cardRefs.current[index];
                        if (!card) return;
                        const scale = (perspective / (perspective + depth)) * shrink;
                        card.style.transformOrigin = '50% 0';
                        card.style.transform = `translate3d(0, ${screenTop - index * CARD_TOP_OFFSET}px, 0) scale(${scale})`;
                        card.style.height = `${height}px`;
                    },
                    commit: () => {},
                    dispose: restoreCards,
                };
            }

            /* --- cached measurements: recomputed on resize, never mid-scroll --- */
            let containerH = 0;
            let viewH = 0;
            let slideInPx = 0;
            let navH = 0;
            const overflows: number[] = services.map(() => 0);
            const cardHeights: number[] = services.map(() => 0);
            /** Scroll offset at which each card starts sliding in. */
            const slideStarts: number[] = services.map(() => 0);
            let totalScroll = 0;

            const measure = () => {
                navH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-height')) || 0;
                containerH = container.offsetHeight;
                viewH = window.innerHeight - navH;
                slideInPx = viewH * SLIDE_IN_VH;

                let cursor = 0;
                for (let i = 0; i < services.length; i += 1) {
                    cardHeights[i] = containerH - i * CARD_TOP_OFFSET;
                    const content = contentRefs.current[i];
                    overflows[i] = content ? Math.max(0, content.offsetHeight - cardHeights[i]) : 0;
                    slideStarts[i] = cursor;
                    cursor += (i === 0 ? 0 : slideInPx) + overflows[i];
                }
                totalScroll = cursor;

                stage.resize(container.offsetWidth, containerH);
                setContainerHeight(containerH);
                setWrapperHeight(`${totalScroll + viewH}px`);
            };

            /* --- frame loop ---------------------------------------------------- */
            const slideP: number[] = services.map((_, i) => (i === 0 ? 1 : 0));
            let running = false;
            let onScreen = true;
            let dirty = true;
            /** Whether the settled state has been written out — the last frame must still land. */
            let flushed = false;
            let last = 0;
            let frame = 0;

            const tick = (now: number) => {
                frame = requestAnimationFrame(tick);
                const dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016;
                last = now;

                // One layout read per frame, before any write — never a read/write interleave.
                const scrolled = Math.max(0, navH - wrapper.getBoundingClientRect().top);

                let settled = true;
                for (let i = 0; i < services.length; i += 1) {
                    const target = i === 0 ? 1 : Math.max(0, Math.min(1, (scrolled - slideStarts[i]) / slideInPx));
                    const eased = slideP[i] + (target - slideP[i]) * (1 - Math.exp(-dt / DAMP_TAU));
                    if (Math.abs(target - eased) > 0.0002) settled = false;
                    slideP[i] = Math.abs(target - eased) < 0.0002 ? target : eased;
                }

                if (!dirty && settled && flushed) return;
                dirty = false;
                flushed = settled;

                let newActive = -1;
                for (let i = 0; i < services.length; i += 1) {
                    // How buried this card is: every card above it that has arrived pushes it back.
                    let buried = 0;
                    for (let j = i + 1; j < services.length; j += 1) buried += slideP[j];

                    const depth = buried * DEPTH_STEP;
                    const shrink = 1 - SLIDE_SHRINK * (1 - slideP[i]);
                    const screenTop = i * CARD_TOP_OFFSET + (1 - slideP[i]) * containerH;
                    stage.place(i, screenTop, depth, cardHeights[i], shrink);

                    // Glass only while the card is both travelling and actually in frame — a card
                    // parked below the fold must not hold a backdrop blur open for nothing.
                    const glass = screenTop < containerH ? 1 - slideP[i] : 0;
                    const body = bodyRefs.current[i];
                    const card = cardRefs.current[i];
                    if (glass > 0.01) {
                        // Quantised: a fresh blur radius every frame would make the compositor
                        // rebuild the backdrop each time for a difference nobody can see.
                        const blur = Math.round((glass * GLASS_BLUR) / 2) * 2;
                        const filter = `blur(${blur}px) saturate(${Math.round(100 + 70 * glass)}%)`;
                        if (card) {
                            // Must sit on the transformed element: a 3D transform starts a new
                            // backdrop root, so a filter on the body inside it has nothing to sample.
                            card.style.backdropFilter = filter;
                            card.style.setProperty('-webkit-backdrop-filter', filter);
                            card.style.borderRadius = '10px';
                        }
                        if (body) {
                            body.style.backgroundColor = `rgba(248, 248, 248, ${(1 - GLASS_ALPHA * glass).toFixed(3)})`;
                            body.style.boxShadow =
                                `inset 0 1px 0 0 rgba(255, 255, 255, ${(0.85 * glass).toFixed(3)}), ` +
                                `inset 0 0 0 1px rgba(255, 255, 255, ${(0.4 * glass).toFixed(3)}), ` +
                                `0 ${Math.round(26 * glass)}px ${Math.round(70 * glass)}px rgba(21, 21, 21, ${(0.12 * glass).toFixed(3)})`;
                        }
                    } else if (card?.style.backdropFilter) {
                        clearGlass(i);
                    }

                    const content = contentRefs.current[i];
                    if (content) {
                        const overflow = overflows[i];
                        if (overflow > 0) {
                            const from = slideStarts[i] + (i === 0 ? 0 : slideInPx);
                            const p = Math.max(0, Math.min(1, (scrolled - from) / overflow));
                            content.style.transform = `translate3d(0, ${-p * overflow}px, 0)`;
                        } else {
                            content.style.transform = '';
                        }
                    }

                    if (slideP[i] >= 0.85) newActive = i;
                }
                stage.commit();

                if (newActive !== activeCardRef.current) {
                    cardRefs.current.forEach((card, i) => {
                        if (!card) return;
                        if (i === newActive) card.dataset.active = 'true';
                        else delete card.dataset.active;
                    });
                    activeCardRef.current = newActive;
                }

                if (totalScroll > 0) {
                    const progress = Math.max(0, Math.min(1, scrolled / totalScroll));
                    const ci = progress * (WRAPPER_BG_COLORS.length - 1);
                    const lo = Math.floor(ci);
                    const hi = Math.min(lo + 1, WRAPPER_BG_COLORS.length - 1);
                    wrapper.style.backgroundColor = lerpColor(WRAPPER_BG_COLORS[lo], WRAPPER_BG_COLORS[hi], ci - lo);
                }
            };

            const sync = () => {
                if (onScreen === running) return;
                running = onScreen;
                if (running) {
                    last = 0;
                    dirty = true;
                    frame = requestAnimationFrame(tick);
                } else {
                    cancelAnimationFrame(frame);
                }
            };

            const onScroll = () => {
                dirty = true;
            };
            const onResize = () => {
                measure();
                dirty = true;
            };

            measure();
            onScreen = true;
            sync();

            const visibility = new IntersectionObserver(
                ([entry]) => {
                    onScreen = entry.isIntersecting;
                    sync();
                },
                { rootMargin: '200px' },
            );
            visibility.observe(wrapper);
            // Translated copy changes the content height, so remeasure when it actually changes.
            const contentSize = new ResizeObserver(onResize);
            contentRefs.current.forEach((el) => el && contentSize.observe(el));

            scrollEl.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onResize);

            teardown = () => {
                cancelAnimationFrame(frame);
                visibility.disconnect();
                contentSize.disconnect();
                scrollEl.removeEventListener('scroll', onScroll);
                window.removeEventListener('resize', onResize);
                stage.dispose();
                restoreCards();
            };
        };

        void start();

        return () => {
            disposed = true;
            teardown?.();
        };
    }, [isDesktop]);

    return (
        <>
            {/* ─── Desktop: Scroll-Driven Stacking Cards ─── */}
            <div className="hidden pb-24 lg:block">
                {/* Tall wrapper — provides scroll distance for card animation */}
                <div
                    ref={wrapperRef}
                    style={{
                        height: wrapperHeight,
                        backgroundColor: `rgb(${WRAPPER_BG_COLORS[0].join(', ')})`,
                    }}
                >
                    {/* Tall screens: entire section (header + cards) is sticky */}
                    {/* Short screens: only cards section is sticky */}
                    <div
                        className={isTallScreen ? 'sticky' : 'contents'}
                        style={isTallScreen ? { top: 'var(--nav-height, 76px)' } : undefined}
                    >
                        {/* Section Header */}
                        <div className="px-16 pb-6 pt-10 xl:px-20 2xl:pb-12 2xl:pt-24">
                            <div className="flex flex-row items-end gap-16">
                                <AnimateOnScroll>
                                    <h2 className="text-secondary max-w-2xl text-[44px] font-extrabold leading-[1.1] tracking-tight xl:text-[56px] 2xl:text-[64px]">
                                        {t('services.heading')}
                                    </h2>
                                </AnimateOnScroll>
                                <AnimateOnScroll delay={200}>
                                    <p className="text-[#4E4D4D] max-w-3xl text-[15px] font-medium leading-[2]">
                                        {t('services.subtitle')}
                                    </p>
                                </AnimateOnScroll>
                            </div>
                        </div>

                        {/* Cards section */}
                        <div
                            className={`flex items-center overflow-hidden px-16 xl:px-20 ${!isTallScreen ? 'sticky' : ''}`}
                            style={
                                !isTallScreen
                                    ? { top: 'calc(var(--nav-height, 76px) + 44px)', marginBottom: 200 }
                                    : undefined
                            }
                        >
                            <div ref={cardsContainerRef} className="relative h-[500px] w-full overflow-hidden">
                                {services.map((service, i) => (
                                    <div
                                        key={i}
                                        ref={(el) => {
                                            cardRefs.current[i] = el;
                                        }}
                                        className="absolute will-change-transform"
                                        style={{
                                            top: `${i * CARD_TOP_OFFSET}px`,
                                            left: 0,
                                            right: 0,
                                            zIndex: i + 1,
                                        }}
                                    >
                                        <div
                                            ref={(el) => {
                                                bodyRefs.current[i] = el;
                                            }}
                                            className="relative overflow-hidden rounded-[10px]"
                                            style={{
                                                backgroundColor: service.bgColor,
                                                height:
                                                    containerHeight > 0
                                                        ? `${containerHeight - i * CARD_TOP_OFFSET}px`
                                                        : undefined,
                                                boxShadow: i > 0 ? '0 -8px 30px rgba(0, 0, 0, 0.06)' : undefined,
                                            }}
                                        >
                                            {/* <div
                                                className="pointer-events-none absolute inset-x-0 top-0 rounded-t-[10px]"
                                                style={{
                                                    backgroundColor: service.bgColor,
                                                    height: `${CARD_TOP_OFFSET}px`,
                                                    zIndex: 2,
                                                }}
                                            /> */}
                                            <div
                                                ref={(el) => {
                                                    contentRefs.current[i] = el;
                                                }}
                                                className="flex h-full flex-row will-change-transform"
                                            >
                                                {/* Left Content */}
                                                <div className="flex max-w-[45%] flex-1 flex-col justify-center px-12 py-10">
                                                    <div className="svc-stagger mb-6 flex items-center gap-3">
                                                        <img
                                                            src={service.logo}
                                                            alt={`${service.brandPrefix} ${service.name} icon`}
                                                            className="h-14 w-14 flex-shrink-0 rounded-xl object-contain"
                                                        />
                                                        <h3 className="text-[40px] font-extrabold leading-[1.1] tracking-tight xl:text-[48px]">
                                                            <span className="text-secondary">
                                                                {service.brandPrefix}{' '}
                                                            </span>
                                                            <span className="text-[#C2C2C2]">{service.name}</span>
                                                        </h3>
                                                    </div>

                                                    <p className="svc-stagger svc-stagger-1 text-secondary mb-8 max-w-md text-[15px] leading-[2] font-medium">
                                                        {t(`services.${i}.description`)}
                                                    </p>

                                                    <div className="svc-stagger svc-stagger-2 flex items-center gap-4 font-bold">
                                                        <a
                                                            href={service.ctaLink}
                                                            className="bg-secondary hover:bg-secondary-400 rounded-full px-8 py-3.5 text-[15px] font-medium text-white transition-all duration-200 hover:scale-[1.03] hover:shadow-lg active:scale-[0.98]"
                                                        >
                                                            {t(`services.${i}.cta`)}
                                                        </a>
                                                    </div>
                                                </div>

                                                {/* Center Lines Decoration — top-aligned on odd cards, bottom-aligned on even */}
                                                <div
                                                    className={`svc-stagger svc-stagger-3 flex w-[258px] flex-shrink-0 justify-center ${i % 2 === 0 ? 'mt-[-60px] items-start' : 'mb-[-60px] items-end'}`}
                                                >
                                                    <img
                                                        src="/assets/libs/lines.svg"
                                                        alt=""
                                                        aria-hidden="true"
                                                        className="h-[70%] object-contain opacity-40"
                                                    />
                                                </div>

                                                {/* Right Content — Preview Card */}
                                                <div className="svc-stagger svc-stagger-4 flex flex-1 flex-col items-center justify-center px-8 py-10">
                                                    <div className="w-full max-w-md transition-shadow duration-300">
                                                        <img
                                                            src={service.image}
                                                            alt={`${service.brandPrefix} ${service.name} preview`}
                                                            className="w-full rounded-lg object-contain"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Mobile: Horizontal Snap Carousel ─── */}
            <section className="px-4 py-10 lg:hidden">
                {/* Section Header */}
                <div className="pb-8">
                    <AnimateOnScroll>
                        <h2 className="text-secondary mb-4 text-[28px] font-bold leading-[1.1] tracking-tight sm:text-[36px]">
                            {t('services.heading')}
                        </h2>
                    </AnimateOnScroll>
                    <AnimateOnScroll delay={150}>
                        <p className="text-secondary text-base font-normal leading-relaxed">{t('services.subtitle')}</p>
                    </AnimateOnScroll>
                </div>

                <MobileCarousel />
            </section>
        </>
    );
}
